import { authedFetch } from '../../../lib/authedFetch';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Bot,
  TrendingUp,
  Users,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  PieChart,
  Activity,
  Zap,
  Globe,
  RefreshCw,
  Mic,
  Phone,
  PhoneMissed,
  Clock,
  Timer,
  ShieldAlert
} from 'lucide-react';
import { BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { buildAILanguageDistributionModel, buildAIIntentDistributionModel, buildAIEscalationDistributionModel } from '../../../lib/chartModels';
import { useToast } from '../../../hooks/use-toast';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';

const AIMetricsDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [voiceStats, setVoiceStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Honest state: a failed/forbidden fetch must render "unavailable", never a full board of fake zeros.
  const [loadError, setLoadError] = useState(false);
  const { toast } = useToast();
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  useEffect(() => {
    if (currentBusiness?.brandId) fetchMetrics();
    else setLoading(false);
  }, [currentBusiness?.brandId]);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const businessId = currentBusiness?.brandId;
      if (!businessId) { setLoading(false); return; }
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const [chatResponse, voiceResponse] = await Promise.allSettled([
        authedFetch(
          `${backendUrl}/api/v1/ai-chat/analytics/chat-metrics?business_id=${businessId}`
        ),
        authedFetch(
          `${backendUrl}/api/v1/ai-voice/business-stats/${businessId}`
        ),
      ]);

      const chatOk = chatResponse.status === 'fulfilled' && chatResponse.value.ok;
      const voiceOk = voiceResponse.status === 'fulfilled' && voiceResponse.value.ok;
      // Honest: only render metrics that a successful response actually returned. A failed source is
      // null (unavailable), never fabricated zeros.
      setMetrics(chatOk ? await chatResponse.value.json() : null);
      setVoiceStats(voiceOk ? await voiceResponse.value.json() : null);
      setLoadError(!chatOk && !voiceOk);
    } catch (error) {
      console.error('Error fetching AI metrics:', error);
      toast({
        title: t('common.error'),
        description: t('st.aim.loadError'),
        variant: 'destructive',
      });
      setMetrics(null);
      setVoiceStats(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>{t('st.aim.loading')}</p>
        </div>
      </div>
    );
  }

  // Honest unavailable state: no successful response for the primary chat metrics means NO numbers
  // (never a fake zero board). A retry re-runs the fetch.
  if (loadError || !metrics) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-sm">
          <p className="text-red-600 font-medium mb-1">{t('st.aim.loadError')}</p>
          <p className="text-gray-500 text-sm mb-4">{t('common.tryAgain') || 'Please try again.'}</p>
          <button onClick={fetchMetrics} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded text-sm">
            <RefreshCw className="w-4 h-4" /> {t('common.refresh') || 'Refresh'}
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  // Prepare data for charts (pure models in lib/chartModels, unit-tested per instance).
  const languageData = buildAILanguageDistributionModel(metrics.language_distribution);
  const intentData = buildAIIntentDistributionModel(metrics.intent_distribution);
  const escalationData = buildAIEscalationDistributionModel(metrics.escalation_by_level);

  const getResolutionStatus = () => {
    if (metrics.auto_resolution_rate >= 80) return { label: t('st.aim.excellent'), color: 'bg-green-100 text-green-800' };
    if (metrics.auto_resolution_rate >= 50) return { label: t('st.aim.good'), color: 'bg-yellow-100 text-yellow-800' };
    return { label: t('st.aim.needsImprovement'), color: 'bg-red-100 text-red-800' };
  };

  const resolutionStatus = getResolutionStatus();

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bot className="w-8 h-8 text-blue-600" />
            {t('nav.aiPerformance')}
          </h1>
          <p className="text-gray-600 mt-1 flex items-center gap-2">
            {t('st.aim.subtitle')}
            <Badge className={resolutionStatus.color}>{resolutionStatus.label}</Badge>
          </p>
        </div>
        <Button onClick={fetchMetrics} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Conversations */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
              <BarChart3 className="w-4 h-4" />
              {t('st.aim.totalConversations')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold">{metrics.total_conversations}</p>
                <p className="text-xs text-gray-500 mt-1">{t('st.aim.aiProcessedChats')}</p>
              </div>
              <MessageSquare className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        {/* Auto-Resolution Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('st.aim.autoResRate')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-green-600">{metrics.auto_resolution_rate}%</p>
                <p className="text-xs text-gray-500 mt-1">{t('st.aim.nResolved', { count: metrics.auto_resolved_count })}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        {/* Escalation Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
              {metrics.escalation_rate > 30 && <AlertTriangle className="w-4 h-4 text-orange-500" />}
              {t('st.aim.escalationRate')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-orange-600">{metrics.escalation_rate}%</p>
                <p className="text-xs text-gray-500 mt-1">{t('st.aim.nEscalated', { count: metrics.escalated_count })}</p>
              </div>
              <Users className="w-10 h-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        {/* Average Confidence */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('st.aim.avgConfidence')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-600">{metrics.average_confidence != null ? metrics.average_confidence : '—'}</p>
                <p className="text-xs text-gray-500 mt-1">{t('st.aim.intentAccuracy')}</p>
              </div>
              <Activity className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Language Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              {t('st.aim.languageDistribution')}
            </CardTitle>
            <CardDescription className="flex items-center gap-1">
              <PieChart className="w-3 h-3" />
              {t('st.aim.convByLanguage')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {languageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPie>
                  <Pie
                    data={languageData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {languageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                {t('st.aim.noLanguageData')}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Intent Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              {t('st.aim.intentDistribution')}
            </CardTitle>
            <CardDescription>{t('st.aim.customerIntents')}</CardDescription>
          </CardHeader>
          <CardContent>
            {intentData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={intentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                {t('st.aim.noIntentData')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Escalation Breakdown */}
      {escalationData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              {t('st.aim.escalationBreakdown')}
            </CardTitle>
            <CardDescription>{t('st.aim.escalationByLevel')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={escalationData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* AI Voice Metrics */}
      {voiceStats && (
        <>
          <div className="flex items-center gap-2 mt-2">
            <Mic className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-bold">{t('st.aim.aiVoiceAnalytics')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {t('st.aim.totalVoiceCalls')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold">{voiceStats.total_calls}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('st.aim.allInboundCalls')}</p>
                  </div>
                  <Mic className="w-10 h-10 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <Bot className="w-4 h-4" />
                  {t('st.aim.aiHandled')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-blue-600">{voiceStats.ai_handled}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('st.aim.noHumanTransfer')}</p>
                  </div>
                  <Bot className="w-10 h-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  {t('st.aim.aiResolved')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-green-600">{voiceStats.ai_resolved}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('st.aim.confirmedResolved')}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  {voiceStats.escalation_rate > 30 && <AlertTriangle className="w-4 h-4 text-orange-500" />}
                  {t('st.aim.humanEscalated')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-orange-600">{voiceStats.human_escalated}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('st.aim.escalationRatePct', { rate: voiceStats.escalation_rate })}</p>
                  </div>
                  <Users className="w-10 h-10 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <Phone className="w-4 h-4 text-yellow-500" />
                  {t('st.aim.pendingCallbacks')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-yellow-600">{voiceStats.pending_callbacks}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('st.aim.awaitingCallback')}</p>
                  </div>
                  <Phone className="w-10 h-10 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <PhoneMissed className="w-4 h-4 text-red-500" />
                  {t('st.aim.overdueCallbacks')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-red-600">{voiceStats.overdue_callbacks}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('st.aim.pastSlaDeadline')}</p>
                  </div>
                  <PhoneMissed className="w-10 h-10 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {t('st.aim.avgCallDuration')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-indigo-600">{voiceStats.avg_call_duration}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('st.aim.minutesPerCall')}</p>
                  </div>
                  <Clock className="w-10 h-10 text-indigo-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  <Timer className="w-4 h-4" />
                  {t('st.aim.avgResTime')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-teal-600">{voiceStats.avg_resolution_time}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('st.aim.createResolve')}</p>
                  </div>
                  <Timer className="w-10 h-10 text-teal-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {voiceStats.callback_sla_breaches > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-red-700 flex items-center gap-1">
                  <ShieldAlert className="w-4 h-4" />
                  {t('st.aim.slaBreaches')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-red-600">{voiceStats.callback_sla_breaches}</p>
                <p className="text-xs text-red-500 mt-1">{t('st.aim.slaBreachesNote')}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default AIMetricsDashboard;